import React, { createContext, useContext, useState, useEffect } from 'react';
import { signIn as cognitoSignIn, signOut as cognitoSignOut, signUp as cognitoSignUp, confirmSignUp as cognitoConfirmSignUp, fetchUserAttributes as cognitoFetchUserAttributes, resetPassword, confirmResetPassword } from '@aws-amplify/auth';
import '../aws-exports'; // Import to configure Amplify
import LoginModalWrapper from '../components/LoginModalWrapper';
import ErrorBoundary from '../components/ErrorBoundary';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check for existing Cognito session on mount
    checkCurrentSession();
  }, []);

  const checkCurrentSession = async () => {
    try {
      const currentUser = await cognitoFetchUserAttributes();
      
      const userData = {
        email: currentUser.email,
        name: currentUser.name || currentUser.email.split('@')[0],
        picture: currentUser.picture || `https://ui-avatars.com/api/?name=${currentUser.name || currentUser.email.split('@')[0]}&background=random`,
        sub: currentUser.sub,
        username: currentUser.username
      };
      
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      // No current session
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Listen for auth login events (for backward compatibility)
  useEffect(() => {
    const handleAuthLogin = (event) => {
      const mockUser = event.detail;
      setUser(mockUser);
      setIsAuthenticated(true);
    };

    window.addEventListener('auth-login', handleAuthLogin);
    
    return () => {
      window.removeEventListener('auth-login', handleAuthLogin);
    };
  }, []);

  const signIn = async (email, password) => {
    setIsLoading(true);
    try {
      // Check if there's already a signed-in user and sign them out first
      try {
        await cognitoSignOut();
        console.log('Signed out existing user before new sign in');
      } catch (signOutError) {
        console.warn('No existing user to sign out:', signOutError);
      }
      
      // Debug: Log parameters before calling cognitoSignIn
      console.log('Calling cognitoSignIn with:', { email, passwordLength: password?.length });
      
      const cognitoUser = await cognitoSignIn({
        username: email,
        password: password
      });
      
      // Debug: Log successful sign in
      console.log('Sign in response:', cognitoUser);
      
      // Check if sign-in requires confirmation
      if (cognitoUser.nextStep && cognitoUser.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        console.log('Account needs confirmation');
        return { 
          success: false, 
          error: 'Please confirm your email first. Check your inbox for a verification code.',
          needsConfirmation: true
        };
      }
      
      // Get user attributes - handle potential identity pool issues
      let userAttributes;
      try {
        userAttributes = await cognitoFetchUserAttributes(cognitoUser);
        console.log('User attributes retrieved:', userAttributes);
      } catch (attrError) {
        console.warn('Could not fetch user attributes, using basic info:', attrError);
        // Fall back to basic user info without attributes
        userAttributes = {
          email: email,
          name: email.split('@')[0],
          sub: cognitoUser.username || email,
          username: cognitoUser.username || email
        };
      }
      
      const userData = {
        email: userAttributes.email || email,
        name: userAttributes.name || email.split('@')[0],
        picture: userAttributes.picture || `https://ui-avatars.com/api/?name=${userAttributes.name || email.split('@')[0]}&background=random`,
        sub: userAttributes.sub || cognitoUser.username,
        username: userAttributes.username || cognitoUser.username
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Sign in error:', error);
      let errorMessage = 'An error occurred during sign in';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found';
      } else if (error.code === 'NotAuthorizedException') {
        if (error.message.includes('Unauthenticated access')) {
          errorMessage = 'Please confirm your email first. Check your inbox for a verification code.';
        } else if (error.message.includes('already a signed in user')) {
          errorMessage = 'You are already signed in. Please sign out first and try again.';
        } else {
          errorMessage = 'Incorrect username or password';
        }
      } else if (error.code === 'UserNotConfirmedException') {
        errorMessage = 'Please confirm your email first. Check your inbox for a verification code.';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await cognitoSignOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
    
    setUser(null);
    setIsAuthenticated(false);
  };

  const signUp = async (email, password, name) => {
    setIsLoading(true);
    try {
      const { user } = await cognitoSignUp({
        username: email,
        password,
        attributes: {
          email,
          name: name || email.split('@')[0]
        },
        clientMetadata: {
          // Add any additional metadata if needed
        }
      });
      
      return { success: true, user };
    } catch (error) {
      console.error('Sign up error:', error);
      let errorMessage = 'An error occurred during sign up';
      
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists';
      } else if (error.code === 'InvalidParameterException') {
        errorMessage = 'Invalid parameters provided';
      } else if (error.code === 'NotAuthorizedException' && error.message.includes('SECRET_HASH')) {
        errorMessage = 'Authentication configuration error. Please contact support.';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSignUp = async (email, code) => {
    setIsLoading(true);
    try {
      // Debug: Log parameters
      console.log('Confirm sign up - email:', email, 'code:', code);
      
      if (!email || !email.trim()) {
        throw new Error('Email is required for confirmation');
      }
      
      if (!code || !code.trim()) {
        throw new Error('Verification code is required');
      }
      
      // Use correct AWS Amplify v6 parameter format
      await cognitoConfirmSignUp({
        username: email.trim(),
        confirmationCode: code.trim()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Confirmation error:', error);
      let errorMessage = 'An error occurred during confirmation';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired';
      } else if (error.message.includes('username is required')) {
        errorMessage = 'Email is required for confirmation. Please try signing up again.';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    setIsLoading(true);
    try {
      await resetPassword(email);
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      let errorMessage = 'An error occurred';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordSubmit = async (email, code, newPassword) => {
    setIsLoading(true);
    try {
      await confirmResetPassword(email, code, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'An error occurred';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
    signUp,
    confirmSignUp,
    forgotPassword,
    forgotPasswordSubmit,
    checkCurrentSession
  };

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={value}>
        <LoginModalWrapper />
        {children}
      </AuthContext.Provider>
    </ErrorBoundary>
  );
};
